import { Form, Input, Select } from '@seiue/ui'
import { Button, Radio } from 'antd'
import { useSetAtom } from 'jotai'
import { FC, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import {
  CourseOutlineTaskReq,
  CourseOutlineTaskTypeEnum,
  courseApi,
} from 'packages/sdks-next/growp'
import { EntryComponentsWrapper } from 'packages/theme'

import { courseOutlineTaskTypeEnumToDownloadLabel } from '@/features/UserTasks/Course/utils'
import { AttachmentFile } from '@/features/types'
import { ExerciseSetting } from '@/uikit/QuotePaper/ExerciseSetting'
import { courseOutlineTaskTypeEnumToLabel } from '@/utils/course'
import { getTheme } from '@/utils/theme'

import { Tips } from './Styled'
import { UploadTaskFile } from './UploadTaskFile'
import { validFileExistAtom } from './atom'

/**
 * 编辑任务组件
 *
 * @param param0 - 参数
 * @param param0.close - 关闭弹窗
 * @param param0.task - 任务
 * @param param0.outlineId - 大纲id
 * @param param0.taskLength - 任务长度，创建时用来确定新建的 task 的 weight 值
 * @returns 编辑任务组件
 */
export const EditTask: FC<{
  close: () => void
  task?: CourseOutlineTaskReq
  outlineId?: number
  taskLength?: number
}> = ({ close, outlineId, task, taskLength }) => {
  const theme = getTheme()
  const [form] = Form.useForm()

  const setValidFileExist = useSetAtom(validFileExistAtom)

  const isEdit = !!task

  const [paperId, setPaperId] = useState<number | undefined>()

  const typeOptions = courseOutlineTaskTypeEnumToLabel.listOptions(
    'value',
    'label'
  )

  const { api: createCourseOutlineTask } = courseApi.useCreateCourseOutlineTask(
    {
      reload: [courseApi.loadCourseActivityTask],
    }
  )

  const { api: updateCourseOutlineTask } = courseApi.useUpdateCourseOutlineTask(
    {
      reload: [courseApi.loadCourseActivityTask],
    }
  )

  const { data: completion } = courseApi.useQueryExerciseReviewed(
    paperId || 0,
    {
      disable: !paperId,
    }
  )

  const getlimitLevel = useCallback(() => {
    const { submitted, reviewed } = completion || {}
    if (submitted) {
      return reviewed ? 2 : 1
    }
    return 0
  }, [completion])

  useEffect(() => {
    if (task) {
      form.setFieldsValue({
        ...task,
        file: task?.attachments?.[0],
        pid: task.paperId,
      })
      setPaperId(task.paperId)
    }
  }, [task])

  return (
    <Wrapper>
      <Form
        form={form}
        initialValues={{
          type: CourseOutlineTaskTypeEnum.Video,
          allowAttachmentDown: false,
        }}
        colon={false}
        wrapperCol={{ span: 21 }}
        labelCol={{ span: 3 }}
        onFinish={() => {
          form
            .validateFields()
            .then(
              async (values: {
                name?: string
                type: CourseOutlineTaskTypeEnum
              }) => {
                if (!outlineId) return
                const file: AttachmentFile = form.getFieldValue('file')
                const allowAttachmentDown = form.getFieldValue(
                  'allowAttachmentDown'
                )
                const { name, type } = values
                if (
                  [
                    CourseOutlineTaskTypeEnum.Pdf,
                    CourseOutlineTaskTypeEnum.Video,
                  ].includes(type) &&
                  !file
                ) {
                  return
                }
                if (isEdit && task?.id) {
                  await updateCourseOutlineTask({
                    taskId: task?.id,
                    id: outlineId,
                    payload: {
                      ...task,
                      name,
                      allowAttachmentDown: file
                        ? allowAttachmentDown
                        : undefined,
                      type,
                      attachments: file ? [file] : undefined,
                      paperId: paperId,
                    },
                  })
                } else {
                  await createCourseOutlineTask({
                    id: outlineId,
                    payload: {
                      name,
                      allowAttachmentDown: file
                        ? allowAttachmentDown
                        : undefined,
                      type,
                      attachments: file ? [file] : undefined,
                      weight: taskLength,
                      paperId: paperId,
                    },
                  })
                }
                setValidFileExist(false)
                close()
              }
            )
        }}
      >
        <Form.Item
          name="name"
          label={'课程任务标题'}
          rules={[{ required: true, message: '请输入课程任务标题' }]}
        >
          <Input
            placeholder="请输入"
            style={{ width: 360 }}
          />
        </Form.Item>
        <Form.Item
          name="type"
          label={'课程任务类型'}
          rules={[{ required: true, message: '请选择课程任务类型' }]}
        >
          <Select
            options={typeOptions}
            style={{ width: 360 }}
            onChange={() => {
              setValidFileExist(false)
              form.setFieldValue('file', undefined)
            }}
          />
        </Form.Item>
        <Form.Item
          dependencies={['type', 'file']}
          rules={[{ required: true, message: '请上传视频' }]}
          noStyle={true}
        >
          {({ getFieldValue, setFieldValue }) => {
            const type = getFieldValue('type')
            const file = getFieldValue('file')

            const setFile: (file: AttachmentFile | undefined) => void = (
              file
            ) => {
              setFieldValue('file', file)
            }

            return type === CourseOutlineTaskTypeEnum.Exercise ? (
              <Form.Item
                label="习题"
                rules={[
                  {
                    required: true,
                    message: '请创建试卷',
                  },
                ]}
                name="pid"
              >
                <ExerciseSetting
                  value={paperId}
                  onChange={(v) => {
                    setPaperId(v)
                    form.setFieldValue('pid', v)
                  }}
                  limitLevel={getlimitLevel()}
                />
              </Form.Item>
            ) : (
              <Form.Item
                label={
                  type === CourseOutlineTaskTypeEnum.Pdf
                    ? '上传文件'
                    : '录播视频'
                }
              >
                <UploadTaskFile
                  type={type}
                  setFile={setFile}
                  file={file}
                />
              </Form.Item>
            )
          }}
        </Form.Item>
        <Form.Item
          dependencies={['type']}
          label={'下载设置'}
          noStyle={true}
        >
          {({ getFieldValue, setFieldValue }) => {
            const type = getFieldValue('type')
            const defaultValue = task ? task.allowAttachmentDown : false

            return [
              CourseOutlineTaskTypeEnum.Pdf,
              CourseOutlineTaskTypeEnum.Video,
            ].includes(type) ? (
              <Form.Item label={'下载设置'}>
                <div style={{ marginTop: 5 }}>
                  <Radio.Group
                    defaultValue={defaultValue}
                    options={[
                      { label: '不允许下载', value: false },
                      { label: '允许下载', value: true },
                    ]}
                    onChange={(e) => {
                      setFieldValue('allowAttachmentDown', e.target.value)
                    }}
                  />
                  <Tips>{`是否允许成员下载${courseOutlineTaskTypeEnumToDownloadLabel.get(
                    type
                  )}`}</Tips>
                </div>
              </Form.Item>
            ) : null
          }}
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 3, span: 21 }}>
          <div
            style={{
              marginLeft: '6px',
            }}
          >
            <StyleButton
              type="primary"
              htmlType="submit"
              style={{
                marginRight: 8,
                backgroundColor: theme.brand._1,
                border: 'none',
              }}
              onClick={() => {
                setValidFileExist(true)
              }}
            >
              {'保存'}
            </StyleButton>
            <StyleButton
              type="default"
              onClick={() => {
                setValidFileExist(false)
                close()
              }}
            >
              {'取消'}
            </StyleButton>
          </div>
        </Form.Item>
      </Form>
    </Wrapper>
  )
}

const Wrapper = styled(EntryComponentsWrapper)`
  border: 1px solid ${(props) => props.theme.border.greyNormal};
  border-width: 0px 1px 1px 1px;
  background-color: #fff;
  padding: 32px;
  &&& {
    .ant-form-item {
      margin-bottom: 20px;
    }
  }
`

const StyleButton = styled(Button)`
  width: 72px;
  margin-right: 8px;
`
